// Lightweight horizontal-swipe detector for the mobile wizard.
//
// Hook returns a single ref-object of pointer-event handlers to spread
// onto the swipeable container. We use Pointer Events instead of Touch
// Events because Pointer covers mouse + touch + pen uniformly, and
// because the iOS Safari touch-event tax is real.
//
// The hook deliberately ignores vertical-dominant gestures so the user
// can scroll the form column without accidentally triggering a step
// change. Threshold is intentionally generous (60px / 30°) to avoid
// firing on incidental drag.
//
// Opt-out: pointerdowns whose target lives inside an element with
// {@code data-no-swipe} are ignored. The image grids (pending +
// live) set this so drag-to-reorder doesn't get hijacked as a swipe.

import { useRef, type PointerEvent } from "react";

/**
 * Attribute that opts an element + its descendants out of swipe
 * detection. Read by {@link useSwipeNav}'s onPointerDown.
 */
export const NO_SWIPE_ATTR = "data-no-swipe";

interface UseSwipeNavInput {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  /** Minimum X-distance, in CSS pixels, before a swipe registers. */
  threshold?: number;
}

interface UseSwipeNavOutput {
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
}

const DEFAULT_THRESHOLD = 60;
// Y-tolerance: the gesture must travel at most 1.5× the X delta in Y
// for us to count it as a horizontal swipe. Keeps vertical scrolling
// from firing step changes.
const Y_DOMINANCE_RATIO = 1.5;

export function useSwipeNav({
  onSwipeLeft,
  onSwipeRight,
  threshold = DEFAULT_THRESHOLD,
}: UseSwipeNavInput): UseSwipeNavOutput {
  const startRef = useRef<{ x: number; y: number; pointerId: number } | null>(null);

  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    // Only react to primary touch points; ignore mouse + pen + secondary.
    if (event.pointerType !== "touch") return;
    // Bail when the touch started over an element opting out — e.g.
    // sortable image thumbnails. Their drag-to-reorder is a horizontal
    // pointer gesture too; without this check the swipe handler would
    // treat the reorder drag as a step swipe.
    const target = event.target as HTMLElement | null;
    if (target?.closest(`[${NO_SWIPE_ATTR}]`)) return;
    startRef.current = {
      x: event.clientX,
      y: event.clientY,
      pointerId: event.pointerId,
    };
  };

  const onPointerUp = (event: PointerEvent<HTMLElement>) => {
    const start = startRef.current;
    startRef.current = null;
    if (start === null || start.pointerId !== event.pointerId) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < threshold) return;
    if (Math.abs(dy) > Math.abs(dx) * Y_DOMINANCE_RATIO) return;
    if (dx < 0) onSwipeLeft();
    else onSwipeRight();
  };

  const onPointerCancel = (event: PointerEvent<HTMLElement>) => {
    if (startRef.current?.pointerId === event.pointerId) {
      startRef.current = null;
    }
  };

  return { onPointerDown, onPointerUp, onPointerCancel };
}
